import { Flex, Spacing, Tab, TabsGroup, Text } from '@billy/design-system'
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFecImportSessionContext } from '../../contexts/fecImportSessionContext'
import { FecImportMappingTab } from '../../enums/fecImportMappingTab'
import { FecMappingReviewStatus } from '../../enums/fecMappingReviewStatus'
import { useFetchFecAccountMappings } from '../../hooks/useFetchFecAccountMappings'
import { useFetchFecJournalMappings } from '../../hooks/useFetchFecJournalMappings'
import { useFetchFecThirdPartyMappings } from '../../hooks/useFetchFecThirdPartyMappings'
import { useTrackFecImport } from '../../hooks/useTrackFecImport'
import { getFecImportInitialMappingTab } from '../../utils/getFecImportInitialMappingTab'
import { getFecThirdPartyStatusCounts } from '../../utils/getFecThirdPartyStatusCounts'
import { getLockedFecJournalMappingIds } from '../../utils/getLockedFecJournalMappingIds'
import { isFecMappingNextDisabled } from '../../utils/isFecMappingNextDisabled'
import { FecImportMappingActions } from './elements/FecImportMappingActions'
import { FecImportMappingTabContent } from './elements/FecImportMappingTabContent'
import * as Styled from './styles'

const PREVIOUS_MAPPING_TAB: Partial<Record<FecImportMappingTab, FecImportMappingTab>> = {
  [FecImportMappingTab.ChartOfAccounts]: FecImportMappingTab.Journals,
  [FecImportMappingTab.ThirdPartyAccounts]: FecImportMappingTab.ChartOfAccounts,
}

const NEXT_MAPPING_TAB: Partial<Record<FecImportMappingTab, FecImportMappingTab>> = {
  [FecImportMappingTab.Journals]: FecImportMappingTab.ChartOfAccounts,
  [FecImportMappingTab.ChartOfAccounts]: FecImportMappingTab.ThirdPartyAccounts,
}

interface FecImportMappingStepProps {
  onFinaliseSetup: () => void
}

export const FecImportMappingStep = ({ onFinaliseSetup }: FecImportMappingStepProps): ReactElement => {
  const { t } = useTranslation()
  const { onBackFromMappingReview, sessionFecImports: fecImports } = useFecImportSessionContext()
  const [activeTab, setActiveTab] = useState<FecImportMappingTab>(() => getFecImportInitialMappingTab(fecImports))

  const fecImportIds = useMemo(() => fecImports.map((fecImport) => fecImport.id), [fecImports])
  const lockedJournalFecImportIds = useMemo(
    () =>
      new Set(
        fecImports
          .filter((fecImport) => fecImport.journalMappingStatus === FecMappingReviewStatus.Confirmed)
          .map((fecImport) => fecImport.id),
      ),
    [fecImports],
  )
  const journalsResult = useFetchFecJournalMappings(fecImportIds)
  const accountMappingsResult = useFetchFecAccountMappings(fecImportIds)
  const thirdPartyMappingsResult = useFetchFecThirdPartyMappings(fecImportIds)
  const { allJournalMappings, journalMappings } = journalsResult
  const lockedJournalMappingIds = useMemo(
    () =>
      getLockedFecJournalMappingIds({
        allMappings: allJournalMappings,
        displayedMappings: journalMappings,
        lockedFecImportIds: lockedJournalFecImportIds,
      }),
    [allJournalMappings, journalMappings, lockedJournalFecImportIds],
  )
  const { accountMappings } = accountMappingsResult
  const { thirdPartyMappings } = thirdPartyMappingsResult
  const { trackThirdPartyAccountsViewed } = useTrackFecImport()
  const isThirdPartyTabLoaded =
    activeTab === FecImportMappingTab.ThirdPartyAccounts &&
    !thirdPartyMappingsResult.isLoading &&
    !thirdPartyMappingsResult.isError

  useEffect(() => {
    if (isThirdPartyTabLoaded) {
      trackThirdPartyAccountsViewed({ fecImportIds, ...getFecThirdPartyStatusCounts(thirdPartyMappings) })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one event per visit of the tab, not per data refresh
  }, [isThirdPartyTabLoaded, trackThirdPartyAccountsViewed])

  const handleNext = useCallback(() => {
    const nextTab = NEXT_MAPPING_TAB[activeTab]

    if (nextTab) {
      setActiveTab(nextTab)
      return
    }

    onFinaliseSetup()
  }, [activeTab, onFinaliseSetup])

  const handleBack = useCallback(() => {
    const previousTab = PREVIOUS_MAPPING_TAB[activeTab]

    if (previousTab) {
      setActiveTab(previousTab)
      return
    }

    onBackFromMappingReview()
  }, [activeTab, onBackFromMappingReview])

  const handleJournalsTabClick = useCallback(() => {
    setActiveTab(FecImportMappingTab.Journals)
  }, [])

  const handleChartOfAccountsTabClick = useCallback(() => {
    setActiveTab(FecImportMappingTab.ChartOfAccounts)
  }, [])

  const handleThirdPartyAccountsTabClick = useCallback(() => {
    setActiveTab(FecImportMappingTab.ThirdPartyAccounts)
  }, [])

  return (
    <Styled.FecImportMappingStepWrapper>
      <Flex flexDirection="column" gap={Spacing.S8}>
        <Text colorVariant="primary" variant="h2">
          {t('settings.organization.accounting.fec_file_import.modal.mapping_step.title')}
        </Text>
        <Flex flexDirection="column">
          <Text colorVariant="secondary" variant="bodyMD">
            {t('settings.organization.accounting.fec_file_import.modal.mapping_step.description')}
          </Text>
          <Text colorVariant="secondary" variant="bodyMD">
            {t('settings.organization.accounting.fec_file_import.modal.mapping_step.description_later')}
          </Text>
        </Flex>
      </Flex>

      <Styled.FecImportMappingTabs>
        <TabsGroup>
          <Tab
            active={activeTab === FecImportMappingTab.Journals}
            badgeCounter={journalMappings.length}
            onClick={handleJournalsTabClick}
          >
            {t('settings.organization.accounting.fec_file_import.modal.mapping_step.tabs.journals')}
          </Tab>
          <Tab
            active={activeTab === FecImportMappingTab.ChartOfAccounts}
            badgeCounter={accountMappings.length}
            onClick={handleChartOfAccountsTabClick}
          >
            {t('settings.organization.accounting.fec_file_import.modal.mapping_step.tabs.chart_of_accounts')}
          </Tab>
          <Tab
            active={activeTab === FecImportMappingTab.ThirdPartyAccounts}
            badgeCounter={thirdPartyMappings.length}
            onClick={handleThirdPartyAccountsTabClick}
          >
            {t('settings.organization.accounting.fec_file_import.modal.mapping_step.tabs.third_party_accounts')}
          </Tab>
        </TabsGroup>
      </Styled.FecImportMappingTabs>

      <Styled.FecImportMappingTabPanel>
        <FecImportMappingTabContent
          accountMappingsResult={accountMappingsResult}
          activeTab={activeTab}
          journalsResult={journalsResult}
          lockedJournalMappingIds={lockedJournalMappingIds}
          thirdPartyMappingsResult={thirdPartyMappingsResult}
        />
      </Styled.FecImportMappingTabPanel>

      <FecImportMappingActions
        isNextDisabled={isFecMappingNextDisabled({
          accountMappings: accountMappingsResult,
          activeTab,
          journals: journalsResult,
          thirdPartyMappings: thirdPartyMappingsResult,
        })}
        onBack={handleBack}
        onNext={handleNext}
      />
    </Styled.FecImportMappingStepWrapper>
  )
}
